import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    Dialog, 
    DialogContent, 
    DialogHeader, 
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog';
import { 
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
} from '../components/ui/alert-dialog';
import { presetsAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    Plus, 
    Edit2,
    Trash2,
    Loader2,
    Upload,
    Download
} from 'lucide-react';

const defaultPreset = {
    name: '',
    type: 'fixed',
    value: 0,
    requires_reason: false,
    default_note: '',
    sort_order: 0,
    active: true
};

export const AdminPresets = () => {
    const navigate = useNavigate();
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPreset, setEditingPreset] = useState(null);
    const [formData, setFormData] = useState(defaultPreset);

    useEffect(() => {
        fetchPresets();
    }, []);

    const fetchPresets = async () => {
        try {
            const response = await presetsAPI.list();
            setPresets(response.data);
        } catch (error) {
            console.error('Failed to fetch presets:', error);
            toast.error('Failed to load presets');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (preset = null) => {
        if (preset) {
            setEditingPreset(preset);
            setFormData(preset);
        } else {
            setEditingPreset(null);
            setFormData({ ...defaultPreset, sort_order: presets.length + 1 });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Preset name is required');
            return;
        }

        setSaving(true);
        try {
            if (editingPreset) {
                await presetsAPI.update(editingPreset.id, formData);
                toast.success('Preset updated!');
            } else {
                await presetsAPI.create(formData);
                toast.success('Preset created!');
            }
            setDialogOpen(false);
            fetchPresets();
        } catch (error) {
            console.error('Failed to save preset:', error);
            toast.error('Failed to save preset');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await presetsAPI.delete(id);
            toast.success('Preset deleted');
            fetchPresets();
        } catch (error) {
            toast.error('Failed to delete preset');
        }
    };

    const handleExport = async () => {
        try {
            const response = await presetsAPI.export();
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'presets.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Presets exported!');
        } catch (error) {
            toast.error('Failed to export presets');
        }
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                await presetsAPI.import(event.target?.result);
                toast.success('Presets imported!');
                fetchPresets();
            } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to import presets');
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-4">
                    <Skeleton className="h-8 w-48" />
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-20 rounded-lg" />
                    ))}
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate('/settings')}
                        className="h-10 w-10"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
                            Adjustment Presets
                        </h1>
                        <p className="text-slate-500 text-sm">{presets.length} presets</p>
                    </div>
                    <Button
                        onClick={() => handleOpenDialog()}
                        className="h-10 bg-red-600 hover:bg-red-700 text-white"
                        data-testid="add-preset-btn"
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                    </Button>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={handleExport}
                        className="flex-1 h-10"
                        data-testid="export-presets-btn"
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Export CSV
                    </Button>
                    <label className="flex-1">
                        <Button
                            variant="outline"
                            className="w-full h-10"
                            asChild
                        >
                            <span>
                                <Upload className="h-4 w-4 mr-2" />
                                Import CSV
                            </span>
                        </Button>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleImport}
                            className="hidden"
                        />
                    </label>
                </div>

                {/* Presets List */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-0">
                        {presets.length > 0 ? (
                            <div className="divide-y divide-slate-100">
                                {presets.map((preset) => (
                                    <div
                                        key={preset.id}
                                        className={`p-4 ${!preset.active ? 'opacity-50' : ''}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="font-semibold text-slate-900">{preset.name}</p>
                                                    {!preset.active && (
                                                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">
                                                            Inactive
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-slate-500 truncate">
                                                    {preset.default_note || 'No default note'}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="font-bold text-slate-900">
                                                    {preset.type === 'fixed' ? `£${preset.value}` : `${preset.value}%`}
                                                </span>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleOpenDialog(preset)}
                                                    className="h-8 w-8"
                                                    data-testid={`edit-preset-${preset.id}`}
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </Button>
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            data-testid={`delete-preset-${preset.id}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent>
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>Delete preset?</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                This will permanently delete "{preset.name}".
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                            <AlertDialogAction
                                                                onClick={() => handleDelete(preset.id)}
                                                                className="bg-red-600 hover:bg-red-700"
                                                            >
                                                                Delete
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-8 text-center">
                                <p className="text-slate-500">No presets yet</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Preset Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl uppercase tracking-tight">
                            {editingPreset ? 'Edit Preset' : 'Add Preset'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Name *
                            </Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="e.g., High ceilings uplift"
                                className="h-12 border-slate-300"
                                data-testid="preset-name-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Type
                                </Label>
                                <Select 
                                    value={formData.type}
                                    onValueChange={(v) => setFormData({ ...formData, type: v })}
                                >
                                    <SelectTrigger className="h-12" data-testid="preset-type-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="fixed">Fixed (£)</SelectItem>
                                        <SelectItem value="percent">Percent (%)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Value
                                </Label>
                                <Input
                                    type="number"
                                    value={formData.value}
                                    onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                                    className="h-12 border-slate-300"
                                    data-testid="preset-value-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Default Note
                            </Label>
                            <Input
                                value={formData.default_note}
                                onChange={(e) => setFormData({ ...formData, default_note: e.target.value })}
                                placeholder="Note shown when preset is applied..."
                                className="h-12 border-slate-300"
                                data-testid="preset-note-input"
                            />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm font-medium">Requires reason</span>
                            <Switch
                                checked={formData.requires_reason}
                                onCheckedChange={(v) => setFormData({ ...formData, requires_reason: v })}
                                data-testid="preset-requires-reason"
                            />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm font-medium">Active</span>
                            <Switch
                                checked={formData.active}
                                onCheckedChange={(v) => setFormData({ ...formData, active: v })}
                                data-testid="preset-active"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase"
                            data-testid="save-preset-btn"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    Saving...
                                </>
                            ) : editingPreset ? (
                                'Update Preset'
                            ) : (
                                'Create Preset'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};
